
/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
*/
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from '../i18n/useTranslation';
import { UserProfile, SavedOutfit, RetailItem, SavedContent, Lookbook, OutfitLayer, AgentAction, AgentResponse } from '../types';
import { XIcon, SendIcon, SparklesIcon, CalendarIcon, ShoppingBagIcon } from '../components/icons';
import { generateSocialMediaContent } from '../services/geminiService';
import { BookingDetails } from '../types';
import Spinner from '../components/Spinner';
import { agentServiceClient } from '../services/agentServiceClient';

interface AgentingWorkflowChatProps {
    isOpen: boolean;
    onClose: () => void;
    userProfile: UserProfile;
    setUserProfile: React.Dispatch<React.SetStateAction<UserProfile>>;
    findSimilarItems: (item: { name: string, url: string }) => Promise<{ results: RetailItem[] }>;
    addToBasket: (item: RetailItem) => void;
    submitBooking: (details: BookingDetails) => Promise<{ confirmation: string; date: string }>;
    savedOutfits: SavedOutfit[];
    baseModelImageUrl: string | null;
    onSaveContent: (content: Omit<SavedContent, 'id' | 'createdAt'>) => void;
    onSaveLookbook: (lookbook: Omit<Lookbook, 'id' | 'createdAt'>) => void;
    currentLayer: OutfitLayer;
    onOpenColorPicker: () => boolean;
}



interface Message {
    id: string;
    role: 'user' | 'agent';
    agentRole?: string;
    text: string;
    type?: 'text' | 'products' | 'confirmation' | 'plan';
    data?: any;
    isDelegated?: boolean;
    statusSteps?: { message: string, status: 'complete' | 'in-progress' | 'error' }[];
}

export const AgentingWorkflowChat: React.FC<AgentingWorkflowChatProps> = ({
    isOpen,
    onClose,
    userProfile,
    setUserProfile,
    findSimilarItems,
    addToBasket,
    submitBooking,
    savedOutfits,
    baseModelImageUrl,
    onSaveContent,
    onSaveLookbook,
    currentLayer,
    onOpenColorPicker
}) => {
    const { t, lang } = useTranslation();
    const [messages, setMessages] = useState<Message[]>([
        { id: '1', role: 'agent', agentRole: 'stylist', text: t('agentWorkflow_greeting') }
    ]);
    const [input, setInput] = useState('');
    const [isThinking, setIsThinking] = useState(false);
    const [currentStatus, setCurrentStatus] = useState<string>('');
    const [activeAgentRole, setActiveAgentRole] = useState<string>('stylist');

    const handleAction = (action: AgentAction) => {
        switch (action.type) {
            case 'OPEN_COLOR_PICKER':
                onOpenColorPicker();
                break;
            case 'SHOW_PRODUCTS':
                // Handled via message data for now
                break;
            case 'BOOK_SESSION':
                // Could trigger the booking modal
                break;
            case 'ADD_TO_BASKET':
                addToBasket(action.payload);
                break;
            default:
                console.warn("Unhandled agent action:", action.type);
        }
    };

    const handleSendMessage = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!input.trim() || isThinking) return;

        const userText = input;
        setInput('');
        setMessages(prev => [...prev, { id: Date.now().toString(), role: 'user', text: userText }]);
        setIsThinking(true);
        setCurrentStatus(t('agentWorkflow_thinking'));

        try {
            const context = { userProfile, currentLayer, savedOutfits };
            const response: AgentResponse = await agentServiceClient.processMessage(userText, context);

            setActiveAgentRole(response.role);

            const newMessage: Message = {
                id: Date.now().toString(),
                role: 'agent',
                agentRole: response.role,
                text: response.text,
                isDelegated: !!response.delegatedTo,
                statusSteps: response.statusSteps
            };

            // Map actions to UI types if needed
            if (response.actions) {
                response.actions.forEach(action => {
                    handleAction(action);
                    if (action.type === 'SHOW_PRODUCTS') {
                        newMessage.type = 'products';
                        newMessage.data = action.payload;
                    }
                });
            }

            setMessages(prev => [...prev, newMessage]);

        } catch (error) {
            setMessages(prev => [...prev, { id: Date.now().toString(), role: 'agent', text: t('agentWorkflow_error') }]);
        } finally {
            setIsThinking(false);
            setCurrentStatus('');
        }
    };

    if (!isOpen) return null;

    return (
        <motion.div
            initial={{ opacity: 0, x: 300 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 300 }}
            className="fixed inset-y-0 right-0 w-96 bg-white dark:bg-gray-800 shadow-2xl z-50 flex flex-col border-l border-gray-200 dark:border-gray-700"
        >
            <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center bg-indigo-600 text-white">
                <h2 className="font-bold flex items-center gap-2"><SparklesIcon className="w-5 h-5" /> {t('agentWorkflow_title')}</h2>
                <button onClick={onClose} className="hover:bg-indigo-700 p-1 rounded-full" title={t('common_close')}><XIcon className="w-6 h-6" /></button>
            </div>

            <div className="flex-grow overflow-y-auto p-4 space-y-4 bg-gray-50 dark:bg-gray-900/50 custom-scrollbar">
                {messages.map((msg) => (
                    <div key={msg.id} className={`flex flex-col ${msg.role === 'user' ? 'items-end' : 'items-start'}`}>
                        {msg.role === 'agent' && (
                            <span className="text-[10px] font-bold uppercase tracking-widest text-indigo-600 dark:text-indigo-400 mb-1 ml-1 flex items-center gap-1">
                                {msg.agentRole === 'stylist' ? 'Styling Agent' : msg.agentRole === 'shopper' ? 'Shopping Assistant' : 'Assistant'}
                                {msg.isDelegated && <span className="normal-case font-normal opacity-60">(Collaborating...)</span>}
                            </span>
                        )}
                        <div className={`max-w-[85%] p-3 rounded-lg ${msg.role === 'user' ? 'bg-indigo-600 text-white' : 'bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-100 border border-gray-200 dark:border-gray-600 shadow-sm'}`}>
                            <p className="text-sm whitespace-pre-wrap">{msg.text}</p>
                            {msg.type === 'products' && msg.data && (
                                <div className="mt-3 space-y-2">
                                    {(msg.data as RetailItem[]).map((item, idx) => (
                                        <div key={idx} className="flex gap-2 bg-gray-50 dark:bg-gray-800 p-2 rounded border border-gray-200 dark:border-gray-600">
                                            <img src={item.imageUrl} alt={item.name} className="w-12 h-12 object-cover rounded" />
                                            <div className="flex-grow min-w-0">
                                                <p className="text-xs font-bold truncate">{item.name}</p>
                                                <p className="text-xs text-gray-500">{item.price}</p>
                                                <button onClick={() => addToBasket(item)} className="mt-1 text-xs bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded hover:bg-indigo-200 w-full flex items-center justify-center gap-1">
                                                    <ShoppingBagIcon className="w-3 h-3" /> Add
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                ))}
                {isThinking && (
                    <div className="flex justify-start">
                        <div className="bg-gray-200 dark:bg-gray-700 p-3 rounded-lg flex flex-col gap-2">
                             <div className="flex items-center gap-2">
                                <Spinner className="w-4 h-4 text-indigo-500" />
                                <span className="text-xs font-semibold text-gray-700 dark:text-gray-200">{currentStatus || t('agentWorkflow_thinking')}</span>
                             </div>
                        </div>
                    </div>
                )}
                {messages[messages.length - 1]?.statusSteps?.map((step, idx) => (
                    <div key={idx} className="flex justify-start ml-4 opacity-70">
                        <div className="flex items-center gap-2 text-[10px] text-gray-500">
                            {step.status === 'complete' ? '✅' : step.status === 'error' ? '❌' : '⏳'}
                            <span>{step.message}</span>
                        </div>
                    </div>
                ))}
            </div>

            <form onSubmit={handleSendMessage} className="p-4 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
                <div className="relative">
                    <input
                        type="text"
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        placeholder={t('agentWorkflow_placeholder')}
                        className="w-full pl-4 pr-10 py-2 border rounded-full dark:bg-gray-700 dark:border-gray-600 dark:text-white focus:ring-2 focus:ring-indigo-500"
                        disabled={isThinking}
                    />
                    <button type="submit" disabled={!input.trim() || isThinking} className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 bg-indigo-600 text-white rounded-full hover:bg-indigo-700 disabled:opacity-50" title={t('common_send')}>
                        <SendIcon className="w-4 h-4" />
                    </button>
                </div>
            </form>
        </motion.div>
    );
};
